import { useEffect } from "react";

import { faTableLayout } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";
import { v4 } from "uuid";

import {
  useCreateDashboard,
  useDashboardQuery,
  useUpdateDashboard,
} from "src/api/analytics-dashboard/dashboard";
import { Label } from "src/base-components/Label";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { IconPicker } from "src/cases/common/IconPicker";
import { WorkspaceMembersSelect } from "src/cases/common/WorkspaceMembersSelect";
import { AVAILABLE_ICONS } from "src/cases/templates/utils";
import type { DashboardMember } from "src/clients/analytics-dashboard";
import {
  DashboardMemberTypeEnum,
  Realm,
} from "src/clients/analytics-dashboard";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

type FormValues = {
  icon: string;
  name: string;
  memberIds: string[];
};

const emptyForm = (icon: string): FormValues => ({
  icon,
  name: "",
  memberIds: [],
});

type RealmFormBehavior = {
  hasMembers: boolean;
  defaultIcon: string;
};

const REALM_FORM_BEHAVIOR: Record<Realm, RealmFormBehavior> = {
  [Realm.CASE_ANALYTICS]: {
    hasMembers: true,
    defaultIcon: "shield-halved",
  },
  [Realm.FLOWS]: {
    hasMembers: false,
    defaultIcon: "chart-mixed",
  },
  [Realm.WORKSPACE]: {
    hasMembers: false,
    defaultIcon: "chart-mixed",
  },
};

export const DashboardFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  dashboardId?: string;
  onCreated?: (dashboardId: string) => void;
  realm: Realm;
  realmId: string;
}> = ({
  open,
  onClose,
  afterLeave,
  dashboardId,
  onCreated,
  realm,
  realmId,
}) => {
  const isEditMode = !!dashboardId;
  const { hasMembers, defaultIcon } = REALM_FORM_BEHAVIOR[realm];

  const { mutateAsync: createDashboard, isPending: isCreating } =
    useCreateDashboard(realm, realmId);

  const { data: dashboardData, isLoading: isLoadingDashboard } =
    useDashboardQuery(realm, realmId, dashboardId, {
      enabled: isEditMode,
    });
  const { commitAsync: updateDashboard, isPending: isUpdating } =
    useUpdateDashboard(realm, realmId, dashboardId);

  const isLoading = isEditMode ? isLoadingDashboard : false;
  const isPending = isEditMode ? isUpdating : isCreating;

  const form = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: emptyForm(defaultIcon),
  });

  // Reset form when dashboard data loads in edit mode
  useEffect(() => {
    if (isEditMode && dashboardData) {
      form.reset({
        icon: dashboardData.icon ?? defaultIcon,
        name: dashboardData.name ?? "",
        memberIds: dashboardData.members?.map((m) => m.id) ?? [],
      });
    }
  }, [dashboardData, isEditMode, form, defaultIcon]);

  const toMembers = (ids: string[]): DashboardMember[] =>
    ids.map((id) => ({ type: DashboardMemberTypeEnum.USER, id }));

  const onSubmit = async (data: FormValues) => {
    try {
      if (isEditMode) {
        if (!dashboardData) return;
        await updateDashboard((current) => ({
          ...current,
          ...(hasMembers && { members: toMembers(data.memberIds) }),
          name: data.name || null,
          icon: data.icon || null,
        }));
        toastActions.success({
          title: "Dashboard updated",
          description: "Your dashboard has been updated.",
        });
      } else {
        const newId = v4();
        await createDashboard({
          dashboardId: newId,
          members: hasMembers ? toMembers(data.memberIds) : undefined,
          name: data.name || undefined,
          icon: data.icon || undefined,
        });
        toastActions.success({
          title: "Dashboard created",
          description: "Your new dashboard is ready.",
        });
        onCreated?.(newId);
      }

      onClose();
    } catch (error) {
      logger.error(error);
      const action = isEditMode ? "update" : "create";

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: `Failed to ${action} dashboard`,
          description:
            "Someone made changes while you were editing. Please refresh and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: `Failed to ${action} dashboard`,
          description:
            error.response?.data.detail.msg ??
            "An unknown error occurred. Please try again later.",
        });
        return;
      }

      toastActions.failure({
        title: `Failed to ${action} dashboard`,
        description: "An unknown error occurred. Please try again later.",
      });
    }
  };

  return (
    <Modal
      afterLeave={() => {
        form.reset(emptyForm(defaultIcon));
        afterLeave?.();
      }}
      open={open}
      onClose={onClose}
    >
      <Modal.Header>{isEditMode ? "Edit" : "Create"} dashboard</Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          {isLoading ? (
            <div className="flex flex-col content-stretch items-start justify-start gap-5">
              <div className="flex w-full items-start justify-start gap-2">
                <Skeleton height="h-15" width="w-15" />
                <div className="flex flex-1 flex-col">
                  <Label required>Name</Label>
                  <Skeleton height="h-8" width="w-full" />
                </div>
              </div>
              {hasMembers && (
                <div className="w-full">
                  <Label mb="mb-0">Select members</Label>
                  <p className="mb-2 font-inter-normal-12px text-gray-500">
                    Choose who can view and edit this dashboard. Only the
                    members you add here will have access.
                  </p>
                  <Skeleton height="h-10" width="w-full" />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col content-stretch items-start justify-start gap-5">
              <div className="flex w-full items-start justify-start gap-2">
                <Controller
                  control={form.control}
                  defaultValue={defaultIcon}
                  name="icon"
                  render={({ field }) => (
                    <IconPicker
                      options={AVAILABLE_ICONS}
                      placeholder={faTableLayout}
                      size="md"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <div className="flex flex-1 flex-col">
                  <Label required>Name</Label>
                  <Input
                    error={!!form.formState.errors.name}
                    placeholder="Enter dashboard name"
                    {...form.register("name", {
                      required: "Dashboard name is required",
                      maxLength: {
                        value: DEFAULT_STRING_LENGTH,
                        message: `Dashboard name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                      },
                    })}
                  />
                  {form.formState.errors.name?.message && (
                    <ErrorHint>{form.formState.errors.name.message}</ErrorHint>
                  )}
                </div>
              </div>
              {hasMembers && (
                <div className="w-full">
                  <Label mb="mb-0">Select members</Label>
                  <p className="mb-2 font-inter-normal-12px text-gray-500">
                    Choose who can view and edit this dashboard. Only the
                    members you add here will have access.
                  </p>
                  <Controller
                    control={form.control}
                    defaultValue={[]}
                    name="memberIds"
                    render={({ field }) => (
                      <WorkspaceMembersSelect
                        dataLoc="dashboard-members-select"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              aria-label={isEditMode ? "Save dashboard" : "Create dashboard"}
              dataLoc="modal-primary-button"
              isDisabled={
                isPending ||
                (isEditMode && !dashboardData) ||
                !form.formState.isValid ||
                (isEditMode && !form.formState.isDirty)
              }
              htmlType="submit"
              isLoading={isPending}
              variant="primary"
            >
              {isEditMode ? "Save" : "Create"}
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
